/**
 * companySubtreeQuery — direct Firestore subtree enumeration for ANY user.
 *
 * Unlike `companySubtreeClient.fetchCompanySubtree` (which calls the
 * company-ADMIN-gated `listCompanySubtree` callable), this reads the
 * `companies` collection directly with `where('path', 'array-contains', self)`.
 * That query is authorized for EVERY authenticated user whose `companyId` is
 * in a doc's `path[]` — i.e. their own company and every descendant — by the
 * `match /companies/{companyId}` rule's query-safe `resource.data.path` arm
 * (see firestore.rules). It NEVER returns ancestors/siblings/other tenants.
 *
 * Used by the #122 dashboard data-scope selector so a non-admin parent-org
 * user can still detect + pick among their sub-companies. Mirrors the store's
 * private `resolveSubtreeCompanyIds`, but returns full `Company` records (for
 * display names) rather than just ids.
 *
 * Degrades to `[]` on any error (permission / offline / solo user) so the
 * caller treats "no subtree" as "selector hidden" — never throws.
 */
import type { Company } from '../types/company';
import { isLocalMockMode } from '../lib/localDev/isLocalMockMode';

export async function fetchSubtreeCompanies(
  companyId: string | null | undefined,
): Promise<Company[]> {
  if (typeof companyId !== 'string' || companyId.trim().length === 0) return [];
  // The local fixture is deliberately provider-free. Do not initialize
  // Firestore (or its retry/diagnostic transports) for a synthetic tenant.
  if (isLocalMockMode()) return [];
  try {
    const { db } = await import('../firebase');
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const snap = await getDocs(
      query(collection(db, 'companies'), where('path', 'array-contains', companyId)),
    );
    const rows: Company[] = [];
    for (const d of snap.docs) {
      const data = d.data() as Company;
      // Defensive: stamp the doc id when the persisted `id` field is missing.
      rows.push({ ...data, id: typeof data.id === 'string' && data.id.length > 0 ? data.id : d.id });
    }
    return rows;
  } catch {
    // permission-denied (solo user), offline, or rules-stale token → no subtree.
    return [];
  }
}
